import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Sparkles, Trophy, Zap, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import ServerCard from '../components/ServerCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';

export default function Home() {
  const [servers, setServers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServers();
    fetchCategories();
  }, [selectedCategory, search]);

  const fetchServers = async () => {
    try {
      const params = {
        limit: 12,
        status_filter: 'approved',
        featured: true,
      };
      if (selectedCategory) params.category = selectedCategory;
      if (search) params.search = search;

      const response = await api.get('/servers', { params });
      setServers(response.data.servers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching servers:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  return (
    <div className="App noise-bg min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at center, rgba(0, 224, 85, 0.15) 0%, rgba(5, 5, 5, 0) 70%)',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E055]/10 border border-[#00E055]/20 rounded-sm text-[#00E055] text-xs font-bold tracking-widest uppercase">
              <Sparkles className="w-4 h-4" />
              THE DIGITAL JUNGLE OF KERALA MINECRAFT
            </div>

            <h1
              className="text-5xl md:text-7xl font-black tracking-tighter uppercase"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              DISCOVER & VOTE
              <br />
              <span className="text-[#00E055]">THE BEST SERVERS</span>
            </h1>

            <p className="text-lg md:text-xl font-medium leading-relaxed text-muted-foreground max-w-2xl mx-auto">
              Join the ultimate Minecraft server community for Kerala. Browse,
              vote, and play on the most exciting servers in the region.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
              <Link to="/servers">
                <Button className="bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold uppercase">
                  <Zap className="w-5 h-5 mr-2" />
                  BROWSE SERVERS
                </Button>
              </Link>

              <Link to="/leaderboard">
                <Button variant="outline">
                  <Trophy className="w-5 h-5 mr-2" />
                  VIEW LEADERBOARD
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Servers Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4 mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span 
                className="text-lg font-black tracking-tight uppercase text-yellow-500" 
                style={{ 
                  textShadow: '1.2px 0 0 rgba(255,0,0,0.7), -1.2px 0 0 rgba(0,255,0,0.7)',
                  fontFamily: 'Unbounded, sans-serif'
                }}
              >
                FEATURED
              </span>
            </div>
            <h2 className="text-4xl font-black tracking-tighter uppercase" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              SERVERS
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-[#00E055] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {servers.map((server) => (
                <ServerCard key={server.id} server={server} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col-reverse md:flex-row justify-between items-center gap-4">

          {/* Copyright (Left Side) */}
          <p className="text-muted-foreground text-center md:text-left text-sm">
            © 2026 MalluRealm. The Digital Jungle of Kerala Minecraft.
          </p>

          {/* Discord (Right Side) */}
          <div className="flex justify-center md:justify-end">
            <a
              href="https://discord.gg/SCycmPeq4y"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#00E055]/10 border border-[#00E055]/30 rounded-md hover:bg-[#00E055]/20 transition-colors"
            >
              <img
                src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/discord.svg"
                alt="Discord"
                className="w-5 h-5"
                style={{
                  filter: 'invert(68%) sepia(93%) saturate(1470%) hue-rotate(87deg) brightness(101%) contrast(103%)',
                }}
              />
              <span className="font-semibold text-[#00E055]">
                Join our Discord
              </span>
            </a>
          </div>

        </div>
      </footer>

    </div>
  );
}
